import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useKeyboard } from '../hooks/useKeyboard';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { subscribeBDs, addBD, updateBD, softDeleteBD, restoreBD, computeStats, exportCSV } from '../utils/firestoreService';
import { groupBDs } from '../utils/groupUtils';
import { STATUTS } from '../utils/constants';
import BDForm from '../components/BDForm';
import BulkAddModal from '../components/BulkAddModal';
import BackupModal from '../components/BackupModal';
import MissingView from '../components/MissingView';
import ShareModal from '../components/ShareModal';
import ToReadView from '../components/ToReadView';
import TrashModal from '../components/TrashModal';
import RafaleScanModal from '../components/RafaleScanModal';
import PhotoBatchModal from '../components/PhotoBatchModal';
import { earnedBadgeIds, describeBadgeId } from '../utils/badges';
import BadgeUnlockOverlay from '../components/BadgeUnlockOverlay';
import Mascot from '../components/Mascot';
import { SkeletonGrid, SkeletonList } from '../components/Skeletons';
import { loadTotals, findMissingTomes, startSeriesSync, stopSeriesSync, useSeriesSettings } from '../utils/seriesTotals';
import Confetti from '../components/Confetti';
import { ALL_SEASONS } from '../utils/seasonalTheme';
import SeasonFX from '../components/SeasonFX';
import BDDetail from '../components/BDDetail';
import { BDCard, BDListRow } from '../components/BDCard';
import SwipeRow from '../components/SwipeRow';
import SeriesView from '../components/SeriesView';
import WishlistView from '../components/WishlistView';
import ProfileModal from '../components/ProfileModal';
import StatsPage from './StatsPage';

const F = "'Inter',-apple-system,BlinkMacSystemFont,sans-serif";
const D = "'Bangers','Arial Black',cursive";

// CSS var helper
const v = name => `var(${name})`;

/* ── Logo ─────────────────────────────────────────────────── */
const Logo = ({ size=28 }) => (
  <svg width={size} height={size} viewBox="0 0 512 512" fill="none">
    <rect width="512" height="512" rx="100" fill="var(--bg)"/>
    <rect width="512" height="512" rx="100" fill="none" stroke="var(--border)" strokeWidth="6"/>
    <rect x="64"  y="330" width="384" height="58" rx="10" fill="#F04E1A"/>
    <rect x="64"  y="330" width="12"  height="58" rx="4"  fill="#C83E12"/>
    <rect x="80"  y="264" width="352" height="58" rx="10" fill="#F5C842"/>
    <rect x="80"  y="264" width="12"  height="58" rx="4"  fill="#D4A800"/>
    <rect x="64"  y="198" width="384" height="58" rx="10" fill="#3B6FBF"/>
    <rect x="64"  y="198" width="12"  height="58" rx="4"  fill="#2A509A"/>
    <rect x="96"  y="132" width="280" height="58" rx="10" fill="#5A9E7A"/>
    <rect x="96"  y="132" width="12"  height="58" rx="4"  fill="#3D7A5A"/>
    <ellipse cx="370" cy="104" rx="90" ry="60" fill="#FFFFFF" stroke="#F04E1A" strokeWidth="8"/>
    <path d="M322 152 L296 188 L338 164 Z" fill="#FFFFFF" stroke="#F04E1A" strokeWidth="8" strokeLinejoin="round" paintOrder="stroke"/>
    <circle cx="340" cy="104" r="10" fill="#F04E1A"/>
    <circle cx="370" cy="104" r="10" fill="#F04E1A"/>
    <circle cx="400" cy="104" r="10" fill="#F04E1A"/>
  </svg>
);

/* ── Toasts ─────────────────────────────────────────────── */
function Toasts({ list }) {
  return (
    <div style={{position:'fixed',bottom:'calc(84px + env(safe-area-inset-bottom))',left:'50%',transform:'translateX(-50%)',zIndex:9999,display:'flex',flexDirection:'column',gap:'8px',alignItems:'center',pointerEvents:'none',width:'calc(100% - 2rem)',maxWidth:'360px'}}>
      {list.map(t=>(
        <div key={t.id} style={{background:t.type==='error'?v('--red'):t.type==='warn'?'#D97706':v('--ink'),color:'#fff',padding:'11px 18px',borderRadius:'12px',fontSize:'14px',fontWeight:600,fontFamily:F,boxShadow:'0 4px 20px rgba(0,0,0,.25)',animation:t.out?'toast-out .3s ease forwards':'toast-in .3s cubic-bezier(.22,1,.36,1) forwards',display:'flex',alignItems:'center',gap:'8px',width:'100%',pointerEvents:'auto'}}>
          <span>{t.type==='error'?'✕':t.type==='warn'?'⚠️':'✓'}</span>
          <span style={{flex:1}}>{t.msg}</span>
          {t.action && (
            <button onClick={t.action.onClick} style={{background:'rgba(255,255,255,.18)',border:'none',borderRadius:'7px',padding:'5px 10px',color:'#fff',fontFamily:F,fontSize:'12.5px',fontWeight:700,cursor:'pointer',flexShrink:0}}>
              {t.action.label}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/* ── Offline banner ─────────────────────────────────────── */
function OfflineBanner() {
  const [offline, setOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const on  = () => setOffline(true);
    const off = () => setOffline(false);
    window.addEventListener('offline', on);
    window.addEventListener('online',  off);
    return () => { window.removeEventListener('offline',on); window.removeEventListener('online',off); };
  }, []);
  if (!offline) return null;
  return (
    <div style={{background:'#92400E',color:'#FEF3C7',padding:'8px 1rem',textAlign:'center',fontSize:'12px',fontWeight:600,fontFamily:F,display:'flex',alignItems:'center',justifyContent:'center',gap:'6px',position:'sticky',top:'56px',zIndex:99}}>
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><circle cx="6.5" cy="6.5" r="5.5" stroke="currentColor" strokeWidth="1.3"/><path d="M6.5 3v3.5M6.5 8.5v.8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
      Hors-ligne — données en cache, modifications synchronisées au retour du réseau
    </div>
  );
}

/* ── Alerte prêts non rendus ────────────────────────────── */
function PretAlert({ bds, onView }) {
  const DELAI = 30;
  const anciens = bds.filter(b => {
    if (b.statut !== 'pret') return false;
    const d = b.updatedAt?.seconds
      ? new Date(b.updatedAt.seconds*1000)
      : b.updatedAt instanceof Date ? b.updatedAt : null;
    return d && (Date.now()-d.getTime()) > DELAI*86400000;
  });
  if (!anciens.length) return null;
  return (
    <div style={{background:'#FFF7ED',borderBottom:'1px solid #FED7AA',padding:'10px 1rem',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'12px',position:'sticky',top:'56px',zIndex:99,flexWrap:'wrap'}}>
      <div style={{fontSize:'13px',fontWeight:600,color:'#C2410C',fontFamily:F,display:'flex',alignItems:'center',gap:'6px'}}>
        📤 <span>{anciens.length} album{anciens.length>1?'s':''} en prêt depuis plus de {DELAI} jours</span>
      </div>
      <button onClick={onView} style={{padding:'5px 12px',border:'1px solid #FDBA74',borderRadius:'99px',background:'#FFF',fontFamily:F,fontSize:'12px',fontWeight:600,color:'#EA580C',cursor:'pointer',flexShrink:0}}>Voir les prêts →</button>
    </div>
  );
}

/* ── Stat pill ──────────────────────────────────────────── */
const Stat = ({ val, label, color }) => (
  <div style={{background:v('--surface'),border:`1px solid ${v('--border')}`,borderRadius:'12px',padding:'10px 14px',textAlign:'center',minWidth:'70px',flexShrink:0,boxShadow:v('--card-shadow')}}>
    <div style={{fontFamily:D,fontSize:'1.55rem',color:color||v('--orange'),letterSpacing:'.03em',lineHeight:1}}>{val}</div>
    <div style={{fontSize:'10px',fontWeight:600,color:v('--muted'),textTransform:'uppercase',letterSpacing:'.06em',marginTop:'3px',fontFamily:F}}>{label}</div>
  </div>
);

/* ── Confirm delete ─────────────────────────────────────── */
const Confirm = ({ bd, onOk, onCancel }) => (
  <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:2100,padding:'1rem',backdropFilter:'blur(6px)'}} onClick={e=>e.target===e.currentTarget&&onCancel()}>
    <div style={{background:v('--surface'),borderRadius:'20px',padding:'2rem 1.5rem',maxWidth:'340px',width:'100%',boxShadow:'0 20px 60px rgba(0,0,0,.3)',animation:'popIn .3s ease',fontFamily:F,textAlign:'center',border:`1px solid ${v('--border')}`}}>
      <div style={{width:'52px',height:'52px',background:v('--orange-pale'),borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 16px',fontSize:'22px'}}>🗑️</div>
      <div style={{fontSize:'17px',fontWeight:700,color:v('--ink'),marginBottom:'8px'}}>Supprimer cet exemplaire ?</div>
      <div style={{fontSize:'14px',color:v('--muted'),marginBottom:'1.5rem',lineHeight:1.5}}>
        <strong style={{color:v('--ink')}}>{bd.titre||bd.serie||'Cette BD'}</strong>{bd.editeur?` (${bd.editeur})`:''} sera supprimé définitivement.
      </div>
      <div style={{display:'flex',gap:'10px'}}>
        <button onClick={onCancel} style={{flex:1,padding:'11px',border:`1px solid ${v('--border')}`,borderRadius:'10px',background:v('--surface'),fontFamily:F,fontSize:'14px',fontWeight:600,color:v('--muted'),cursor:'pointer'}}>Annuler</button>
        <button onClick={onOk} style={{flex:1,padding:'11px',border:'none',borderRadius:'10px',background:v('--red'),color:'#fff',fontFamily:F,fontSize:'14px',fontWeight:700,cursor:'pointer'}}>Supprimer</button>
      </div>
    </div>
  </div>
);

const SORTS = [{v:'serie',l:'Série'},{v:'titre',l:'Titre'},{v:'dateParution',l:'Année'},{v:'note',l:'Note'},{v:'updatedAt',l:'Récent'}];
const PAGE_SIZE = 60;

/* ══════════════════════════════════════════════════════════ */
export default function CollectionPage() {
  const { user, logout } = useAuth();
  const totalsVer = useSeriesSettings();
  useEffect(() => { if (user?.uid) { startSeriesSync(user.uid); return stopSeriesSync; } return undefined; }, [user?.uid]);
  const { toggle: toggleTheme, isDark, season, seasonPref, setSeasonPref, fxLevel, setFxLevel } = useTheme();
  const [showSeasons, setShowSeasons] = useState(false);
  const [bannerHidden, setBannerHidden] = useState(() => { try { return !!localStorage.getItem('bulletheque_banner_'+new Date().toDateString()); } catch { return false; } });

  const [bds, setBds]           = useState([]);
  const [loading, setLoading]   = useState(true);
  const [view, setView]         = useState('grid');   // grid|list|series|stats
  const [search, setSearch]     = useState('');
  const [fStatut, setFStatut]   = useState('');
  const [fSerie, setFSerie]     = useState('');
  const [fGenre, setFGenre]     = useState('');
  const [sort, setSort]         = useState('serie');
  const [sortDesc, setSortDesc] = useState(false);
  const [page, setPage]         = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkPreset, setBulkPreset] = useState(null); // { serie, numeros } depuis la vue Manquants
  const [sharePreset, setSharePreset] = useState(null); // { scope, serie, mode }
  const [showShare, setShowShare] = useState(false);
  const [showBackup, setShowBackup] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const [showRafale, setShowRafale] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [collMode, setCollMode] = useState('grid');      // dernier affichage de la collection
  const [listeMode, setListeMode] = useState('missing'); // dernière sous-page de « Listes »
  const [photoBatch, setPhotoBatch] = useState(null); // null | { serie }
  const [editBD, setEditBD]     = useState(null);
  const [detailGroup, setDetail]= useState(null);
  const [menu, setMenu]         = useState(false);
  const [delBD, setDelBD]       = useState(null);
  const [toasts, setToasts]     = useState([]);
  const [showFilters, setSF]    = useState(false);
  const [showProfile, setProfile] = useState(false);
  const menuRef = useRef();
  const tid = useRef(0);
  const searchRef = useRef(null);

  // ── Raccourcis clavier ─────────────────────────────────────────
  useKeyboard({
    onNew:         () => { setEditBD(null); setShowForm(true); },
    onFocusSearch: () => { searchRef.current?.focus(); },
    onEscape:      () => {
      if (showForm)    { setShowForm(false); setEditBD(null); return; }
      if (showBulk)    { setShowBulk(false); setBulkPreset(null); return; }
      if (showBackup)  { setShowBackup(false); return; }
      if (showTrash)   { setShowTrash(false); return; }
      if (showRafale)  { setShowRafale(false); return; }
      if (photoBatch)  { setPhotoBatch(null); return; }
      if (showAdd)     { setShowAdd(false); return; }
      if (showSeasons) { setShowSeasons(false); return; }
      if (showShare)   { setShowShare(false); return; }
      if (detailGroup) { setDetail(null); return; }
      if (showProfile) { setProfile(false); return; }
      if (showFilters) { setSF(false); return; }
    },
    enabled: !showForm && !showBulk && !showBackup && !showShare && !showTrash && !showRafale && !photoBatch, // pas de conflit avec les champs des formulaires
  });

  const toast = useCallback((msg, type='success', action=null) => {
    const id = ++tid.current;
    setToasts(t=>[...t,{id,msg,type,action,out:false}]);
    setTimeout(()=>setToasts(t=>t.map(x=>x.id===id?{...x,out:true}:x)),2600);
    setTimeout(()=>setToasts(t=>t.filter(x=>x.id!==id)),2900);
  },[]);

  // ── Sync temps réel ──────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    const unsub = subscribeBDs(
      user.uid,
      (data, truncated) => {
        setBds(data); setLoading(false);
        if (truncated) toast(`Collection très volumineuse : seuls les ${data.length} premiers albums sont chargés`, 'error');
      },
      (err)  => { toast('Erreur sync : '+err.message, 'error'); setLoading(false); }
    );
    return unsub; // se désabonne quand le composant se démonte
  }, [user.uid, toast]);

  // ── Badges : détecte un nouveau palier franchi (silencieux au tout premier chargement) ──
  const [badgeQueue, setBadgeQueue] = useState([]);
  const [currentBadge, setCurrentBadge] = useState(null);

  useEffect(() => {
    if (!bds.length) return;
    const earned = earnedBadgeIds(bds, loadTotals());
    const raw = localStorage.getItem('bulletheque_badges_seen_v2');
    if (raw === null) {
      localStorage.setItem('bulletheque_badges_seen_v2', JSON.stringify(earned));
      return;
    }
    let seen = [];
    try { seen = JSON.parse(raw); } catch {}
    const newly = earned.filter(id => !seen.includes(id));
    if (newly.length) {
      const descs = newly.map(describeBadgeId).filter(Boolean);
      setBadgeQueue(q => [...q, ...descs]);
      localStorage.setItem('bulletheque_badges_seen_v2', JSON.stringify(earned));
    }
  }, [bds]);

  // Affiche les badges débloqués un par un (animation plein écran)
  useEffect(() => {
    if (!currentBadge && badgeQueue.length) {
      setCurrentBadge(badgeQueue[0]);
      setBadgeQueue(q => q.slice(1));
    }
  }, [badgeQueue, currentBadge]);

  // ── Confettis : détecte une série qui vient de devenir complète ──
  const [showConfetti, setShowConfetti] = useState(false);
  // Totaux reçus du cloud (autre appareil / restauration) : on met à jour les repères « déjà vu »
  // sans célébrer, sinon un nouvel appareil ferait pleuvoir confettis et badges d'un coup.
  useEffect(() => {
    if (!totalsVer || !bds.length) return;
    const totals = loadTotals();
    try {
      const earned = earnedBadgeIds(bds, totals);
      const seenB = JSON.parse(localStorage.getItem('bulletheque_badges_seen_v2') || '[]');
      localStorage.setItem('bulletheque_badges_seen_v2', JSON.stringify([...new Set([...seenB, ...earned])]));
      const bySerie = {};
      bds.forEach(b => { const k = b.serie?.trim(); if (k) (bySerie[k] = bySerie[k] || []).push(b); });
      const complete = Object.entries(bySerie).filter(([s, l]) => totals[s] && findMissingTomes(l, totals[s]).length === 0).map(([s]) => s);
      const seenC = JSON.parse(localStorage.getItem('bulletheque_complete_series_seen') || '[]');
      localStorage.setItem('bulletheque_complete_series_seen', JSON.stringify([...new Set([...seenC, ...complete])]));
    } catch {}
  }, [totalsVer]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!bds.length) return;
    const totals = loadTotals();
    const bySerie = {};
    bds.forEach(b => { const k = b.serie?.trim(); if (k) (bySerie[k] = bySerie[k] || []).push(b); });
    const completeNow = Object.entries(bySerie)
      .filter(([s,l]) => totals[s] && findMissingTomes(l, totals[s]).length === 0)
      .map(([s]) => s);

    const raw = localStorage.getItem('bulletheque_complete_series_seen');
    if (raw === null) {
      localStorage.setItem('bulletheque_complete_series_seen', JSON.stringify(completeNow));
      return;
    }
    let seen = [];
    try { seen = JSON.parse(raw); } catch {}
    const newlyComplete = completeNow.filter(s => !seen.includes(s));
    if (newlyComplete.length) {
      setShowConfetti(true);
      toast(`🎉 Série complète : ${newlyComplete[0]} !`);
      localStorage.setItem('bulletheque_complete_series_seen', JSON.stringify(completeNow));
    }
  }, [bds, toast]);
  useEffect(()=>{ setPage(1); },[search,fStatut,fSerie,fGenre,sort,sortDesc]);
  useEffect(()=>{
    const h=e=>{ if(menuRef.current&&!menuRef.current.contains(e.target)) setMenu(false); };
    document.addEventListener('mousedown',h);
    return()=>document.removeEventListener('mousedown',h);
  },[]);

  /* ── CRUD ── */
  const handleSave = async (data) => {
    try {
      if (editBD?.id) {
        await updateBD(user.uid, editBD.id, data);
        setBds(p=>p.map(b=>b.id===editBD.id?{...b,...data,updatedAt:new Date()}:b));
        toast('BD modifiée ✓');
      } else {
        const id = await addBD(user.uid, data);
        setBds(p=>[{id,...data,photos:[],createdAt:new Date(),updatedAt:new Date()},...p]);
        toast('BD ajoutée à ta collection 📚');
      }
      setShowForm(false); setEditBD(null);
    } catch(e) { toast('Erreur : '+e.message,'error'); throw e; }
  };

  const handleDel = async () => {
    const bd = delBD; setDelBD(null);
    try {
      await softDeleteBD(user.uid, bd.id);
      setBds(p=>p.filter(b=>b.id!==bd.id));
      setDetail(prev=>{ if(!prev) return null; const r=prev.bds.filter(b=>b.id!==bd.id); return r.length?{...prev,bds:r,ref:r[0]}:null; });
      toast('Album déplacé dans la corbeille.', 'success', {
        label: 'Annuler',
        onClick: async () => {
          try {
            const restored = await restoreBD(user.uid, bd.id);
            setBds(p=>[...p, { ...restored, id: bd.id }]);
            toast('Restauré ✓');
          } catch(e) { toast('Erreur : '+e.message, 'error'); }
        },
      });
    } catch(e) { toast('Erreur : '+e.message,'error'); }
  };


  const quickUpdate = async (bd, patch) => {
    try {
      await updateBD(user.uid, bd.id, patch);
      setBds(p=>p.map(b=>b.id===bd.id?{...b,...patch,updatedAt:new Date()}:b));
    } catch(e) { toast('Erreur : '+e.message,'error'); }
  };

  const openDetail = (g, origin) => setDetail({ ...g, _origin: origin });

  const openEdit = (bd) => { setEditBD(bd); setShowForm(true); };
  const openDuplicate = (bd) => {
    const {id,photos,createdAt,updatedAt,...rest}=bd;
    setEditBD({...rest,statut:'garder'});
    setShowForm(true);
  };

  /* ── Filtres ── */
  const filteredBds = useMemo(() => {
    let r = [...bds];
    if (search) { const s=search.toLowerCase(); r=r.filter(b=>[b.titre,b.serie,b.auteur,b.scenariste,b.dessinateur,b.editeur,b.collection,b.genre,b.notes,b.lieuAchat].some(f=>f?.toLowerCase().includes(s))); }
    if (fStatut) r=r.filter(b=>b.statut===fStatut);
    if (fSerie)  r=r.filter(b=>b.serie===fSerie);
    if (fGenre)  r=r.filter(b=>b.genre===fGenre);
    return r;
  },[bds,search,fStatut,fSerie,fGenre]);

  /* ── Groupes triés ── */
  const allGroups = useMemo(() => {
    const gs = groupBDs(filteredBds);
    gs.sort((a,b)=>{
      let cmp=0; const ra=a.ref,rb=b.ref;
      if (sort==='note') cmp=(rb.note||0)-(ra.note||0);
      else if (sort==='dateParution') cmp=(rb.dateParution||0)-(ra.dateParution||0);
      else if (sort==='updatedAt') { const ta=ra.updatedAt?.seconds||ra.updatedAt?.getTime?.()/1000||0,tb=rb.updatedAt?.seconds||rb.updatedAt?.getTime?.()/1000||0; cmp=tb-ta; }
      else { const va=(ra[sort]||'').toString().toLowerCase(),vb=(rb[sort]||'').toString().toLowerCase(); cmp=va<vb?-1:va>vb?1:0; if(cmp===0) cmp=(parseInt(ra.numero)||0)-(parseInt(rb.numero)||0); }
      return sortDesc?-cmp:cmp;
    });
    return gs;
  },[filteredBds,sort,sortDesc]);

  /* ── Pagination ── */
  const totalPages = Math.max(1, Math.ceil(allGroups.length / PAGE_SIZE));
  const groups     = allGroups.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE);

  // Bandeau "ajouts récents" — uniquement en vue grille, sans filtre actif
  const recentBds = useMemo(() => {
    const toMs = ts => ts?.toMillis ? ts.toMillis() : (ts instanceof Date ? ts.getTime() : 0);
    return [...bds].sort((a,b)=>toMs(b.createdAt)-toMs(a.createdAt)).slice(0,10);
  }, [bds]);
  const showRecentBanner = view==='grid' && !loading && page===1 && !search && !fStatut && !fSerie && !fGenre && recentBds.length>=3;
  const hasMore    = page < totalPages;

  const stats   = useMemo(()=>computeStats(bds),[bds]);
  const series  = useMemo(()=>[...new Set(bds.map(b=>b.serie).filter(Boolean))].sort(),[bds]);
  const genres  = useMemo(()=>[...new Set(bds.map(b=>b.genre).filter(Boolean))].sort(),[bds]);
  const hasFilters = search||fStatut||fSerie||fGenre;
  const pctLus  = stats.total>0?Math.round(stats.lus/stats.total*100):0;

  const ini = user?.displayName ? user.displayName.split(' ').map(n=>n[0]).join('').toUpperCase().slice(0,2) : user?.email?.[0]?.toUpperCase()||'?';

  const selStyle = {padding:'9px 12px',border:`1px solid ${v('--border')}`,borderRadius:'8px',fontFamily:F,fontSize:'13px',fontWeight:500,background:v('--surface'),color:v('--ink'),outline:'none',cursor:'pointer',width:'100%'};

  return (
    <div style={{minHeight:'100vh',background:'transparent',fontFamily:F}}>
      <OfflineBanner/>
      <PretAlert bds={bds} onView={()=>{setFStatut('pret');setView('list');setSF(true);}}/>

      {/* ══ HEADER ══════════════════════════════════════════ */}
      <header style={{background:v('--header-bg'),backdropFilter:'blur(16px)',WebkitBackdropFilter:'blur(16px)',borderBottom:`1px solid ${v('--border')}`,position:'sticky',top:0,zIndex:100,boxShadow:'0 1px 0 rgba(0,0,0,.06)'}}>
        <div style={{maxWidth:'1100px',margin:'0 auto',padding:'0 1rem',height:'56px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'10px'}}>
          <div style={{display:'flex',alignItems:'center',gap:'9px',flexShrink:0}}>
            <Logo size={28}/>
            <span style={{fontFamily:D,fontSize:'1.5rem',letterSpacing:'.05em',color:v('--orange'),lineHeight:1}} className="header-title-text">Bulléthèque</span>
            {season && (
              <span title={season.label} style={{fontSize:'17px',lineHeight:1,animation:'seasonBob 2.4s ease-in-out infinite',flexShrink:0}}>{season.emoji}</span>
            )}
          </div>

          <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
            {/* Avatar */}
            <div ref={menuRef} style={{position:'relative'}}>
              <button onClick={()=>setMenu(m=>!m)}
                style={{width:'36px',height:'36px',borderRadius:'50%',background:'linear-gradient(135deg,var(--orange),var(--yellow))',border:`2px solid ${v('--surface')}`,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',overflow:'hidden',padding:0,boxShadow:'0 2px 8px rgba(0,0,0,.15)',fontFamily:D,fontSize:'.95rem',color:'#fff',flexShrink:0}}>
                {user?.photoURL?<img src={user.photoURL} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>:ini}
              </button>
              {menu&&(
                <div style={{position:'absolute',top:'44px',right:0,background:v('--surface'),border:`1px solid ${v('--border')}`,borderRadius:'14px',boxShadow:'0 8px 28px rgba(0,0,0,.2)',padding:'6px',minWidth:'200px',zIndex:200,animation:'popIn .2s ease'}}>
                  <div style={{padding:'10px 12px 8px',borderBottom:`1px solid ${v('--border')}`,marginBottom:'4px'}}>
                    <div style={{fontSize:'14px',fontWeight:700,color:v('--ink')}}>{user?.displayName||'Collectionneur'}</div>
                    <div style={{fontSize:'12px',color:v('--muted'),marginTop:'1px'}}>{user?.email}</div>
                  </div>
                  <MenuItem onClick={()=>{setSharePreset(null);setShowShare(true);setMenu(false);}}>📤 Partager ma collection</MenuItem>
                  <MenuItem onClick={()=>{exportCSV(bds);toast('Export CSV téléchargé !');setMenu(false);}}>📥 Exporter en CSV</MenuItem>
                  <MenuItem onClick={()=>{setShowSeasons(true);setMenu(false);}}>🎨 Thème saisonnier{seasonPref==='off'?' (désactivé)':''}</MenuItem>
                  <MenuItem onClick={()=>{toggleTheme();setMenu(false);}}>{isDark?'☀️ Mode clair':'🌙 Mode sombre'}</MenuItem>
                  <button onClick={()=>{setProfile(true);setMenu(false);}} style={{width:'100%',padding:'9px 12px',border:'none',borderRadius:'8px',background:'transparent',fontFamily:F,fontSize:'13px',fontWeight:600,color:v('--ink'),cursor:'pointer',display:'flex',alignItems:'center',gap:'8px',textAlign:'left'}}
                    onMouseOver={e=>e.currentTarget.style.background=v('--surface2')}
                    onMouseOut={e=>e.currentTarget.style.background='transparent'}>
                    👤 Mon profil
                  </button>
                  <button onClick={()=>{setShowBackup(true);setMenu(false);}} style={{width:'100%',padding:'9px 12px',border:'none',borderRadius:'8px',background:'transparent',fontFamily:F,fontSize:'13px',fontWeight:600,color:v('--ink'),cursor:'pointer',display:'flex',alignItems:'center',gap:'8px',textAlign:'left'}}
                    onMouseOver={e=>e.currentTarget.style.background=v('--surface2')}
                    onMouseOut={e=>e.currentTarget.style.background='transparent'}>
                    💾 Sauvegarde / restauration
                  </button>
                  <button onClick={()=>{setShowTrash(true);setMenu(false);}} style={{width:'100%',padding:'9px 12px',border:'none',borderRadius:'8px',background:'transparent',fontFamily:F,fontSize:'13px',fontWeight:600,color:v('--ink'),cursor:'pointer',display:'flex',alignItems:'center',gap:'8px',textAlign:'left'}}
                    onMouseOver={e=>e.currentTarget.style.background=v('--surface2')}
                    onMouseOut={e=>e.currentTarget.style.background='transparent'}>
                    🗑️ Corbeille
                  </button>
                  <div style={{height:'1px',background:v('--border'),margin:'4px 0'}}/>
                  <button onClick={()=>{logout();setMenu(false);}}
                    style={{width:'100%',padding:'9px 12px',border:'none',borderRadius:'8px',background:'transparent',fontFamily:F,fontSize:'13px',fontWeight:600,color:v('--red'),cursor:'pointer',display:'flex',alignItems:'center',gap:'8px',textAlign:'left'}}
                    onMouseOver={e=>e.currentTarget.style.background='var(--orange-pale)'}
                    onMouseOut={e=>e.currentTarget.style.background='transparent'}>
                    🚪 Déconnexion
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Bandeau saisonnier */}
      {season && !bannerHidden && view!=='stats' && (
        <div style={{maxWidth:'1100px',margin:'10px auto 0',padding:'0 1rem'}}>
          <div style={{display:'flex',alignItems:'center',gap:'10px',background:v('--orange-pale'),border:`1px solid ${v('--orange')}33`,borderRadius:'12px',padding:'9px 12px',color:v('--ink'),fontFamily:F,fontSize:'13px',fontWeight:600,lineHeight:1.35}}>
            <span style={{fontSize:'20px'}}>{season.emoji}</span>
            <span style={{flex:1}}>{season.msg}</span>
            <button aria-label="Masquer" onClick={()=>{setBannerHidden(true);try{localStorage.setItem('bulletheque_banner_'+new Date().toDateString(),'1');}catch{}}}
              style={{border:'none',background:'transparent',color:v('--muted'),fontSize:'18px',cursor:'pointer',padding:'0 4px',lineHeight:1}}>×</button>
          </div>
        </div>
      )}


      <main style={{maxWidth:'1100px',margin:'0 auto',padding:'1rem 1rem 9rem'}}>

        {/* ══ STATS PILLS ═════════════════════════════════════ */}
        {(view==='grid'||view==='list') && (
          <div style={{display:'flex',gap:'8px',overflowX:'auto',paddingBottom:'4px',marginBottom:'1.25rem',scrollbarWidth:'none'}}>
            <Stat val={stats.total}  label="Albums"/>
            <Stat val={stats.series} label="Séries"  color={v('--purple')}/>
            <Stat val={`${pctLus}%`} label="Lus"     color={v('--green')}/>
            {STATUTS.map(s=>(stats.parStatut[s.value]||0)>0?<Stat key={s.value} val={stats.parStatut[s.value]} label={s.label} color={s.color}/>:null)}
            {stats.valeurAchat>0&&<Stat val={`${Math.round(stats.valeurAchat)}€`} label="Investi" color={v('--muted')}/>}
            {stats.valeurVente>0&&<Stat val={`${Math.round(stats.valeurVente)}€`} label="Revente" color={v('--green')}/>}
          </div>
        )}

        {/* ══ SOUS-ONGLETS (Listes) ═════════════════════════ */}
        {(view==='missing'||view==='toread'||view==='wishlist') && (
          <div style={{display:'flex',gap:'4px',marginBottom:'1rem',background:v('--surface2'),borderRadius:'12px',padding:'4px',border:`1px solid ${v('--border')}`}}>
            {[{id:'missing',icon:<MissingIcon/>,label:'Manquants'},{id:'toread',icon:<ToReadIcon/>,label:'À lire'},{id:'wishlist',icon:<WishIcon/>,label:'Souhaits'}].map(t=>(
              <button key={t.id} onClick={()=>{setView(t.id);setListeMode(t.id);}}
                style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:'6px',padding:'10px 4px',border:'none',borderRadius:'9px',cursor:'pointer',background:view===t.id?v('--surface'):'transparent',color:view===t.id?v('--orange'):v('--muted'),fontFamily:F,fontSize:'14px',fontWeight:700,boxShadow:view===t.id?v('--card-shadow'):'none'}}>
                {t.icon}{t.label}
              </button>
            ))}
          </div>
        )}

        {/* ══ TOOLBAR (grille/liste) ══════════════════════════ */}
        {(view==='grid'||view==='list') && (
          <>
            <div style={{display:'flex',gap:'8px',alignItems:'center',marginBottom:'10px'}}>
              <div style={{position:'relative',flex:1}}>
                <svg style={{position:'absolute',left:'12px',top:'50%',transform:'translateY(-50%)',pointerEvents:'none',color:v('--subtle')}} width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5"/><path d="M11 11L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <input type="text" value={search} placeholder="Titre, série, auteur, notes…" onChange={e=>setSearch(e.target.value)}
                  style={{width:'100%',padding:'10px 12px 10px 36px',border:`1px solid ${v('--border')}`,borderRadius:'10px',fontFamily:F,fontSize:'14px',background:v('--surface'),color:v('--ink'),outline:'none',boxShadow:v('--card-shadow')}}
                  onFocus={e=>{e.target.style.borderColor='var(--orange)';e.target.style.boxShadow='0 0 0 3px rgba(240,78,26,.12)';}}
                  onBlur={e=>{e.target.style.borderColor='var(--border)';e.target.style.boxShadow='var(--card-shadow)';}}/>
                {search&&<button onClick={()=>setSearch('')} style={{position:'absolute',right:'10px',top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:v('--muted'),fontSize:'18px',lineHeight:1,padding:'2px'}}>×</button>}
              </div>
              <button onClick={()=>setSF(f=>!f)}
                style={{display:'flex',alignItems:'center',gap:'5px',padding:'10px 14px',border:`1px solid ${hasFilters?v('--orange'):v('--border')}`,borderRadius:'10px',background:hasFilters?v('--orange-pale'):v('--surface'),color:hasFilters?v('--orange'):v('--muted'),fontFamily:F,fontSize:'13px',fontWeight:600,cursor:'pointer',whiteSpace:'nowrap'}}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 3h12M3 7h8M5 11h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
                Filtres{hasFilters?' ●':''}
              </button>
              <button onClick={()=>{const n=view==='grid'?'list':'grid';setView(n);setCollMode(n);}} title={view==='grid'?'Passer en liste':'Passer en grille'} aria-label={view==='grid'?'Afficher en liste':'Afficher en grille'}
                style={{width:'42px',height:'42px',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',border:`1px solid ${v('--border')}`,borderRadius:'10px',background:v('--surface'),color:v('--muted'),cursor:'pointer'}}>
                {view==='grid'?<ListIcon/>:<GridIcon/>}
              </button>
            </div>

            {showFilters&&(
              <div style={{background:v('--surface'),border:`1px solid ${v('--border')}`,borderRadius:'12px',padding:'14px',marginBottom:'10px',display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:'10px',boxShadow:v('--card-shadow')}}>
                <div>
                  <div style={{fontSize:'11px',fontWeight:600,color:v('--muted'),marginBottom:'5px',textTransform:'uppercase',letterSpacing:'.05em'}}>Statut</div>
                  <select style={selStyle} value={fStatut} onChange={e=>setFStatut(e.target.value)}><option value="">Tous</option>{STATUTS.map(s=><option key={s.value} value={s.value}>{s.emoji} {s.label}</option>)}</select>
                </div>
                {series.length>0&&<div>
                  <div style={{fontSize:'11px',fontWeight:600,color:v('--muted'),marginBottom:'5px',textTransform:'uppercase',letterSpacing:'.05em'}}>Série</div>
                  <select style={selStyle} value={fSerie} onChange={e=>setFSerie(e.target.value)}><option value="">Toutes</option>{series.map(s=><option key={s} value={s}>{s}</option>)}</select>
                </div>}
                {genres.length>0&&<div>
                  <div style={{fontSize:'11px',fontWeight:600,color:v('--muted'),marginBottom:'5px',textTransform:'uppercase',letterSpacing:'.05em'}}>Genre</div>
                  <select style={selStyle} value={fGenre} onChange={e=>setFGenre(e.target.value)}><option value="">Tous</option>{genres.map(g=><option key={g} value={g}>{g}</option>)}</select>
                </div>}
                {hasFilters&&<div style={{display:'flex',alignItems:'flex-end'}}>
                  <button onClick={()=>{setFStatut('');setFSerie('');setFGenre('');setSearch('');}} style={{width:'100%',padding:'9px',border:`1px solid ${v('--border')}`,borderRadius:'8px',background:v('--surface'),fontFamily:F,fontSize:'13px',fontWeight:600,color:v('--muted'),cursor:'pointer'}}>Effacer tout</button>
                </div>}
              </div>
            )}

            <div style={{display:'flex',gap:'5px',flexWrap:'wrap',marginBottom:'1rem',alignItems:'center'}}>
              <span style={{fontSize:'11px',fontWeight:600,color:v('--subtle')}}>Trier :</span>
              {SORTS.map(s=>(
                <button key={s.v} onClick={()=>{ if(sort===s.v)setSortDesc(d=>!d); else{setSort(s.v);setSortDesc(false);} }}
                  style={{padding:'5px 11px',border:`1px solid ${sort===s.v?v('--orange'):v('--border')}`,borderRadius:'99px',background:sort===s.v?v('--orange-pale'):v('--surface'),color:sort===s.v?v('--orange'):v('--muted'),fontFamily:F,fontSize:'12px',fontWeight:sort===s.v?700:500,cursor:'pointer',display:'flex',alignItems:'center',gap:'4px'}}>
                  {s.l}{sort===s.v&&<span style={{fontSize:'10px'}}>{sortDesc?'↑':'↓'}</span>}
                </button>
              ))}
              <span style={{marginLeft:'auto',fontSize:'12px',fontWeight:500,color:v('--subtle'),whiteSpace:'nowrap'}}>
                {allGroups.length} album{allGroups.length!==1?'s':''}
                {allGroups.some(g=>g.bds.length>1)&&<span style={{color:v('--orange'),fontSize:'11px'}}> · {allGroups.filter(g=>g.bds.length>1).length} en ×N</span>}
              </span>
            </div>
          </>
        )}

        {/* ══ CONTENU ════════════════════════════════════════ */}
        {loading?(
          view==='list' ? <SkeletonList/> : <SkeletonGrid/>

        ) : view==='series' ? (
          <SeriesView bds={bds} onEditBD={openEdit}/>

        ) : view==='missing' ? (
          <MissingView bds={bds} onToast={toast}
            onAddMissing={(serie,numeros)=>{setBulkPreset({serie,numeros});setShowBulk(true);}}
            onShareMissing={(serie)=>{setSharePreset({scope:'serie',serie,mode:'manquant'});setShowShare(true);}}/>
        ) : view==='toread' ? (
          <ToReadView bds={bds}
            onOpenDetail={(bd,origin)=>openDetail({key:bd.id,ref:bd,bds:[bd]},origin)}
            onEdit={openEdit} onDelete={setDelBD} onToggleLu={bd=>quickUpdate(bd,{lu:!bd.lu})}/>
        ) : view==='stats' ? (
          <StatsPage bds={bds} isDark={isDark}/>
        ) : view==='wishlist' ? (
          <WishlistView onToast={toast}/>

        ) : groups.length===0 ? (
          <div style={{textAlign:'center',padding:'4rem 2rem',background:v('--surface'),border:`1px solid ${v('--border')}`,borderRadius:'16px',boxShadow:v('--card-shadow')}}>
            <Mascot variant={bds.length===0?'happy':'search'} size={110}/>
            <div style={{fontFamily:D,fontSize:'1.8rem',letterSpacing:'.04em',color:v('--orange'),marginBottom:'8px'}}>{bds.length===0?'Ta collection t\'attend !':'Aucun résultat'}</div>
            <div style={{fontSize:'14px',color:v('--muted'),fontWeight:500,marginBottom:'1.5rem'}}>{bds.length===0?'Commence par ajouter ta première BD.':'Essaie de modifier tes filtres.'}</div>
            {bds.length===0&&<button onClick={()=>{setEditBD(null);setShowForm(true);}} style={{padding:'11px 24px',border:'none',borderRadius:'99px',background:v('--orange'),color:'#fff',fontFamily:F,fontSize:'14px',fontWeight:600,cursor:'pointer',boxShadow:'0 4px 14px rgba(240,78,26,.3)'}}>Ajouter ma première BD</button>}
          </div>

        ) : (
          <>
            {showRecentBanner && (
              <div style={{marginBottom:'1.25rem'}}>
                <div style={{fontSize:'11px',fontWeight:700,color:v('--muted'),textTransform:'uppercase',letterSpacing:'.06em',marginBottom:'8px'}}>✨ Ajouts récents</div>
                <div style={{display:'flex',gap:'12px',overflowX:'auto',paddingBottom:'6px',scrollbarWidth:'none'}}>
                  {recentBds.map(bd => (
                    <div key={bd.id} onClick={e=>{const r=e.currentTarget.getBoundingClientRect();openDetail({key:bd.id,ref:bd,bds:[bd]},{top:r.top,left:r.left,width:r.width,height:r.height});}}
                      style={{flexShrink:0,width:'108px',cursor:'pointer'}}>
                      <div style={{width:'108px',height:'150px',borderRadius:'12px',overflow:'hidden',background:bd.photos?.[0]?'#000':'linear-gradient(145deg,var(--orange-pale),var(--surface2))',border:`1px solid ${v('--border')}`,boxShadow:'0 3px 12px rgba(0,0,0,.1)'}}>
                        {bd.photos?.[0]
                          ? <img src={bd.photos[0].url} alt={bd.titre} style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}/>
                          : <div style={{height:'100%',display:'flex',alignItems:'center',justifyContent:'center'}}><span style={{fontSize:'1.8rem',opacity:.45}}>📚</span></div>}
                      </div>
                      <div style={{fontSize:'11px',fontWeight:700,color:v('--ink'),marginTop:'6px',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                        {bd.serie ? `${bd.serie}${bd.numero?` T.${bd.numero}`:''}` : (bd.titre||'(Sans titre)')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {view==='grid' ? (
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:'12px'}}>
                {groups.map((g,i)=>(
                  <div key={g.key} style={{animation:`fadeUp .3s ease ${Math.min(i*.03,.4)}s both`}}>
                    <BDCard bd={g.ref} count={g.bds.length}
                      onClick={e=>{const r=e.currentTarget.getBoundingClientRect();openDetail(g,{top:r.top,left:r.left,width:r.width,height:r.height});}}
                      onDelete={()=>setDelBD(g.ref)}/>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                {groups.map((g,i)=>(
                  <div key={g.key} style={{animation:`fadeUp .25s ease ${Math.min(i*.02,.3)}s both`}}>
                    <SwipeRow
                      onOpen={origin=>openDetail(g,origin)}
                      leftAction={{
                        icon: g.ref.lu ? '↺' : '✓', label: g.ref.lu ? 'Non lu' : 'Lu',
                        color: g.ref.lu ? '#F59E0B' : '#16A34A',
                        onClick: ()=>quickUpdate(g.ref, { lu: !g.ref.lu }),
                      }}
                      rightActions={[
                        { icon:'✏️', label:'Éditer', color:'#3B6FBF', onClick:()=>openEdit(g.ref) },
                        { icon:'🗑️', label:'Suppr.', color:'#DC2626', onClick:()=>setDelBD(g.ref) },
                      ]}>
                      <BDListRow bd={g.ref} count={g.bds.length} onDelete={()=>setDelBD(g.ref)}/>
                    </SwipeRow>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'8px',marginTop:'2rem',flexWrap:'wrap'}}>
                <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}
                  style={{padding:'8px 16px',border:`1px solid ${v('--border')}`,borderRadius:'8px',background:v('--surface'),fontFamily:F,fontSize:'13px',fontWeight:600,color:page===1?v('--subtle'):v('--ink'),cursor:page===1?'not-allowed':'pointer'}}>
                  ← Précédent
                </button>
                <div style={{fontSize:'13px',color:v('--muted'),fontFamily:F,fontWeight:600}}>
                  Page {page} sur {totalPages}
                </div>
                <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={!hasMore}
                  style={{padding:'8px 16px',border:`1px solid ${v('--border')}`,borderRadius:'8px',background:v('--surface'),fontFamily:F,fontSize:'13px',fontWeight:600,color:!hasMore?v('--subtle'):v('--ink'),cursor:!hasMore?'not-allowed':'pointer'}}>
                  Suivant →
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Sélecteur d'ambiance */}
      {showSeasons&&(
        <div onClick={()=>setShowSeasons(false)} style={{position:'fixed',inset:0,zIndex:300,background:'rgba(28,22,18,.45)',display:'flex',alignItems:'flex-end',justifyContent:'center'}}>
          <div onClick={e=>e.stopPropagation()} style={{width:'100%',maxWidth:'520px',maxHeight:'85vh',overflowY:'auto',background:v('--surface'),borderRadius:'20px 20px 0 0',padding:'14px 14px calc(18px + env(safe-area-inset-bottom))'}}>
            <div style={{width:'40px',height:'4px',borderRadius:'9px',background:v('--border'),margin:'0 auto 12px'}}/>
            <div style={{fontFamily:D,fontSize:'1.4rem',letterSpacing:'.04em',color:v('--orange'),margin:'0 4px 4px'}}>🎨 Thème saisonnier</div>
            <div style={{fontSize:'12.5px',color:v('--muted'),margin:'0 4px 10px'}}>« Automatique » suit la date. Tu peux aussi essayer une ambiance.</div>
            <div style={{fontSize:'11px',fontWeight:700,color:v('--muted'),textTransform:'uppercase',letterSpacing:'.05em',margin:'4px 4px 6px'}}>Intensité des effets</div>
            <div style={{display:'flex',gap:'4px',background:v('--surface2'),borderRadius:'12px',padding:'4px',border:`1px solid ${v('--border')}`,marginBottom:'12px'}}>
              {[['off','Aucun'],['low','Léger'],['normal','Normal'],['high','Intense']].map(([id,lab])=>(
                <button key={id} onClick={()=>setFxLevel(id)}
                  style={{flex:1,padding:'9px 2px',border:'none',borderRadius:'9px',cursor:'pointer',fontFamily:F,fontSize:'13px',fontWeight:700,background:fxLevel===id?v('--surface'):'transparent',color:fxLevel===id?v('--orange'):v('--muted'),boxShadow:fxLevel===id?v('--card-shadow'):'none'}}>{lab}</button>
              ))}
            </div>
            {[{id:'auto',emoji:'🗓️',label:'Automatique (selon la date)'},{id:'off',emoji:'🚫',label:'Aucun thème'},...ALL_SEASONS].map(o=>(
              <button key={o.id} onClick={()=>{setSeasonPref(o.id);setBannerHidden(false);try{localStorage.removeItem('bulletheque_banner_'+new Date().toDateString());}catch{}setShowSeasons(false);}}
                style={{width:'100%',display:'flex',alignItems:'center',gap:'12px',padding:'11px 10px',border:'none',borderRadius:'12px',background:seasonPref===o.id?v('--orange-pale'):'transparent',cursor:'pointer',textAlign:'left',fontFamily:F,color:v('--ink'),fontSize:'15px',fontWeight:seasonPref===o.id?700:500}}>
                <span style={{fontSize:'22px',width:'30px',textAlign:'center'}}>{o.emoji}</span>
                <span style={{flex:1}}>{o.label}</span>
                {seasonPref===o.id&&<span style={{color:v('--orange'),fontWeight:700}}>✓</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      <SeasonFX season={season} isDark={isDark} level={fxLevel}/>

      {/* Feuille d'actions « + » */}
      {showAdd&&(
        <div onClick={()=>setShowAdd(false)} style={{position:'fixed',inset:0,zIndex:300,background:'rgba(28,22,18,.45)',display:'flex',alignItems:'flex-end',justifyContent:'center'}}>
          <div onClick={e=>e.stopPropagation()} style={{width:'100%',maxWidth:'520px',background:v('--surface'),borderRadius:'20px 20px 0 0',padding:'14px 14px calc(18px + env(safe-area-inset-bottom))',animation:'popIn .2s ease'}}>
            <div style={{width:'40px',height:'4px',borderRadius:'9px',background:v('--border'),margin:'0 auto 12px'}}/>
            <div style={{fontFamily:D,fontSize:'1.4rem',letterSpacing:'.04em',color:v('--orange'),margin:'0 4px 8px'}}>Ajouter</div>
            {[
              {e:'✏️',t:'Un album',d:'Saisie à la main ou avec le code-barres',f:()=>{setEditBD(null);setShowForm(true);}},
              {e:'📚',t:'Une série entière',d:'Ex : les 72 tomes de Naruto d\'un coup',f:()=>{setBulkPreset(null);setShowBulk(true);}},
              {e:'📷',t:'Scanner des codes-barres',d:'Plusieurs albums à la suite, avec la caméra',f:()=>setShowRafale(true)},
              {e:'📸',t:'Photos en série',d:'Photographier plusieurs albums à la suite',f:()=>setPhotoBatch({serie:''})},
            ].map(a=>(
              <button key={a.t} onClick={()=>{setShowAdd(false);a.f();}}
                style={{width:'100%',display:'flex',alignItems:'center',gap:'14px',padding:'12px 10px',border:'none',borderRadius:'12px',background:'transparent',cursor:'pointer',textAlign:'left',fontFamily:F,color:v('--ink')}}
                onMouseOver={e=>e.currentTarget.style.background=v('--surface2')} onMouseOut={e=>e.currentTarget.style.background='transparent'}>
                <span style={{width:'44px',height:'44px',borderRadius:'12px',background:v('--orange-pale'),display:'flex',alignItems:'center',justifyContent:'center',fontSize:'22px',flexShrink:0}}>{a.e}</span>
                <span style={{minWidth:0}}>
                  <span style={{display:'block',fontSize:'15px',fontWeight:700}}>{a.t}</span>
                  <span style={{display:'block',fontSize:'12.5px',color:v('--muted'),marginTop:'1px'}}>{a.d}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bouton « + » */}
      <button onClick={()=>setShowAdd(true)} aria-label="Ajouter"
        style={{position:'fixed',bottom:'calc(76px + env(safe-area-inset-bottom))',right:'16px',width:'58px',height:'58px',borderRadius:'50%',background:v('--orange'),border:'none',color:'#fff',cursor:'pointer',boxShadow:'0 6px 22px rgba(240,78,26,.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:90}}>
        <svg width="24" height="24" viewBox="0 0 14 14" fill="none"><path d="M7 1v12M1 7h12" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"/></svg>
      </button>

      {/* Barre de navigation */}
      <nav style={{position:'fixed',left:0,right:0,bottom:0,zIndex:95,background:v('--header-bg'),backdropFilter:'blur(16px)',WebkitBackdropFilter:'blur(16px)',borderTop:`1px solid ${v('--border')}`,paddingBottom:'env(safe-area-inset-bottom)'}}>
        <div style={{maxWidth:'600px',margin:'0 auto',display:'flex'}}>
          {[
            {id:'collection',icon:<GridIcon/>,   label:'Collection',on:view==='grid'||view==='list', go:()=>setView(collMode)},
            {id:'series',    icon:<SeriesIcon/>, label:'Séries',    on:view==='series',              go:()=>setView('series')},
            {id:'listes',    icon:<MissingIcon/>,label:'Listes',    on:['missing','toread','wishlist'].includes(view), go:()=>setView(listeMode)},
            {id:'stats',     icon:<StatsIcon/>, label:'Stats',     on:view==='stats',               go:()=>setView('stats')},
          ].map(t=>(
            <button key={t.id} onClick={t.go} aria-current={t.on?'page':undefined}
              style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:'3px',padding:'9px 4px 8px',border:'none',background:'transparent',cursor:'pointer',color:t.on?v('--orange'):v('--muted'),fontFamily:F,fontSize:'11.5px',fontWeight:t.on?700:600}}>
              <span style={{display:'flex',height:'20px',alignItems:'center',transform:'scale(1.45)'}}>{t.icon}</span>
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Modales */}
      {detailGroup&&<BDDetail bds={detailGroup.bds} origin={detailGroup._origin} onClose={()=>setDetail(null)} onEdit={openEdit} onDelete={setDelBD} onAddExemplaire={openDuplicate}/>}
      {showProfile&&<ProfileModal user={user} onClose={()=>setProfile(false)} onSaved={()=>{}}/> }
      {showForm&&<BDForm bd={editBD} onClose={()=>{setShowForm(false);setEditBD(null);}} onSave={handleSave}/>}
      {showBackup&&<BackupModal bds={bds} onClose={()=>setShowBackup(false)} onToast={toast}/>}
      {showTrash&&<TrashModal onClose={()=>setShowTrash(false)} onToast={toast}/>}
      {photoBatch&&<PhotoBatchModal bds={bds} initialSerie={photoBatch.serie} onClose={()=>setPhotoBatch(null)} onToast={toast}/>}
      {showRafale&&<RafaleScanModal onClose={()=>setShowRafale(false)} onToast={toast}/>}
      {showShare&&<ShareModal bds={bds} onClose={()=>{setShowShare(false);setSharePreset(null);}} onToast={toast}
        initialScope={sharePreset?.scope} initialSerie={sharePreset?.serie} initialMode={sharePreset?.mode}/>}
      {showBulk&&<BulkAddModal key={bulkPreset?.serie||'free'} bds={bds} initialSerie={bulkPreset?.serie||''} initialNumeros={bulkPreset?.numeros||null} onClose={()=>{setShowBulk(false);setBulkPreset(null);}} onDone={(n)=>toast(`${n} tome${n>1?'s':''} ajouté${n>1?'s':''} ✓`)}/>}
      {delBD&&<Confirm bd={delBD} onOk={handleDel} onCancel={()=>setDelBD(null)}/>}
      <Toasts list={toasts}/>
      {currentBadge&&<BadgeUnlockOverlay badge={currentBadge} onDone={()=>setCurrentBadge(null)}/>}
      {showConfetti&&<Confetti colors={season?.colors} onDone={()=>setShowConfetti(false)}/>}
    </div>
  );
}

const GridIcon   = ()=><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1" y="1" width="5" height="5" rx="1" fill="currentColor"/><rect x="8" y="1" width="5" height="5" rx="1" fill="currentColor"/><rect x="1" y="8" width="5" height="5" rx="1" fill="currentColor"/><rect x="8" y="8" width="5" height="5" rx="1" fill="currentColor"/></svg>;
const ListIcon   = ()=><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1" y="2" width="12" height="2" rx="1" fill="currentColor"/><rect x="1" y="6" width="12" height="2" rx="1" fill="currentColor"/><rect x="1" y="10" width="12" height="2" rx="1" fill="currentColor"/></svg>;
const SeriesIcon = ()=><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1" y="2" width="3" height="10" rx="1" fill="currentColor"/><rect x="5.5" y="2" width="3" height="10" rx="1" fill="currentColor"/><rect x="10" y="2" width="3" height="10" rx="1" fill="currentColor"/></svg>;
const StatsIcon  = ()=><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1" y="8" width="3" height="5" rx="1" fill="currentColor"/><rect x="5.5" y="5" width="3" height="8" rx="1" fill="currentColor"/><rect x="10" y="2" width="3" height="11" rx="1" fill="currentColor"/></svg>;
const MissingIcon = ()=><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1.5" y="2" width="3" height="10" rx="1" fill="currentColor"/><rect x="9.5" y="2" width="3" height="10" rx="1" fill="currentColor"/><rect x="5.5" y="2.75" width="3" height="8.5" rx="1" stroke="currentColor" strokeWidth="1.2" strokeDasharray="1.6 1.4"/></svg>;
const ToReadIcon  = ()=><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1.5 2.5C1.5 2 2 1.5 3 1.5c1.5 0 3 .5 4 1.5 1-1 2.5-1.5 4-1.5 1 0 1.5.5 1.5 1v9c0-.5-.5-1-1.5-1-1.5 0-3 .5-4 1.5-1-1-2.5-1.5-4-1.5-1 0-1.5.5-1.5 1z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/><path d="M7 3v8.5" stroke="currentColor" strokeWidth="1.2"/></svg>;
const WishIcon   = ()=><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 11.5C7 11.5 1.5 7.8 1.5 4.8a2.5 2.5 0 0 1 5 0 2.5 2.5 0 0 1 5 0c0 3-5.5 6.7-5.5 6.7z" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinejoin="round"/></svg>;

const MenuItem = ({ onClick, children }) => (
  <button onClick={onClick}
    style={{width:'100%',padding:'10px 12px',border:'none',borderRadius:'8px',background:'transparent',fontFamily:"'Inter',-apple-system,sans-serif",fontSize:'13px',fontWeight:600,color:'var(--ink)',cursor:'pointer',display:'flex',alignItems:'center',gap:'8px',textAlign:'left'}}
    onMouseOver={e=>e.currentTarget.style.background='var(--surface2)'}
    onMouseOut={e=>e.currentTarget.style.background='transparent'}>
    {children}
  </button>
);
